import { useEffect, useRef, useState } from 'react';

/**
 * Default progress messages displayed during AI formatting requests.
 * Messages rotate at configurable intervals to indicate system activity.
 * Includes extra messages for longer operations to avoid confusion from looping.
 */
const DEFAULT_PROGRESS_MESSAGES = [
  'Analyzing response...',
  'Improving clarity...',
  'Optimizing formatting...',
  'Finalizing response...',
  'Still processing...',
  'Almost there...',
  'Wrapping up...',
];

const ELAPSED_UPDATE_INTERVAL_MS = 100;

/**
 * Configuration options for progressive loading behavior.
 */
export interface UseProgressiveLoadingOptions {
  delayMs?: number;
  intervalMs?: number;
  messages?: readonly string[];
  /**
   * When set, once the rotation reaches the end of `messages` it
   * cycles only among the last `stickyTailCount` entries instead
   * of wrapping back to index 0. Useful for long-running tasks
   * where the early-progress copy (e.g. "Reading…", "Searching…")
   * shouldn't reappear after we've moved past it — it makes the
   * loader feel like it's stuck if it restarts.
   *
   * Clamped to `[1, messages.length]`. Defaults to `messages.length`,
   * which preserves the original full-cycle behavior.
   */
  stickyTailCount?: number;
}

/**
 * Return type for the useProgressiveLoading hook.
 */
export interface UseProgressiveLoadingResult {
  message: string | null;
  elapsedMs: number;
  /** 0-based index of the currently displayed message, or `null` before
   * the initial delay elapses. Surfaces the rotation position so callers
   * can render a step counter (e.g. "Step 2 of 5") alongside the
   * rotating copy. */
  messageIndex: number | null;
}

/**
 * Hook for displaying Anthropic-style progressive loading messages during long-running operations.
 *
 * After a configurable delay (default 2 seconds), begins cycling through status messages
 * at regular intervals to reassure users that the system is actively working.
 *
 * @param isPending - Whether the async operation is currently in progress
 * @param options - Configuration for delay, interval, and custom messages
 * @returns Current message (or null) and elapsed time in milliseconds
 *
 * @example
 * ```tsx
 * const { message, elapsedMs } = useProgressiveLoading(mutation.isPending);
 *
 * return (
 *   <View>
 *     {mutation.isPending && (
 *       <Text>{message ?? 'Loading...'}</Text>
 *     )}
 *   </View>
 * );
 * ```
 */
const useProgressiveLoading = (
  isPending: boolean,
  options?: UseProgressiveLoadingOptions
): UseProgressiveLoadingResult => {
  const {
    delayMs = 2000,
    intervalMs = 2500,
    messages = DEFAULT_PROGRESS_MESSAGES,
    stickyTailCount,
  } = options ?? {};

  const tailSize = Math.max(1, Math.min(stickyTailCount ?? messages.length, messages.length));

  const tailStart = messages.length - tailSize;

  const [messageIndex, setMessageIndex] = useState<number | null>(null);

  const [elapsedMs, setElapsedMs] = useState(0);

  const startTimeRef = useRef<number | null>(null);

  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);

      delayTimerRef.current = null;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);

      intervalRef.current = null;
    }

    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current);

      elapsedIntervalRef.current = null;
    }

    if (isPending) {
      startTimeRef.current = Date.now();

      elapsedIntervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          setElapsedMs(Date.now() - startTimeRef.current);
        }
      }, ELAPSED_UPDATE_INTERVAL_MS);

      delayTimerRef.current = setTimeout(() => {
        setMessageIndex(0);

        intervalRef.current = setInterval(() => {
          setMessageIndex((prev) => {
            if (prev === null) {
              return 0;
            }

            const next = prev + 1;

            if (next >= messages.length) {
              return tailStart;
            }

            return next;
          });
        }, intervalMs);
      }, delayMs);
    } else {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);

        delayTimerRef.current = null;
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);

        intervalRef.current = null;
      }

      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);

        elapsedIntervalRef.current = null;
      }

      startTimeRef.current = null;

      setMessageIndex(null);

      setElapsedMs(0);
    }

    return () => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
      }
    };
  }, [isPending, delayMs, intervalMs, messages.length, tailStart]);

  const message = messageIndex !== null ? messages[messageIndex] : null;

  return { message, elapsedMs, messageIndex };
};

export default useProgressiveLoading;
